import { observer } from "mobx-react-lite";
import { CustomWidgetSettings } from "./CustomWidgetSettings";
import { useEffect, useState } from "react";
import { CustomWidgetStore } from "./CustomWidgetStore";
import { sendMessageToWorker, workerUrl } from "../../worker";

function resolvePlaceholders(str: string, data: any) {
  return str.replace(/\{\{?\s*([A-Za-z0-9_\.]+?)\s*\}?\}/g, (_, expr) => {
    // support "a.b.c" paths
    const path = expr.trim().split(".");
    let cur = data;
    for (const key of path) {
      cur = cur?.[key];
    }
    const result = cur == null ? expr : String(cur);
    console.log(`replacing ${expr} with ${result}`);
    return result;
  });
}

function resolveJsPlaceholders(str: string, data: any) {
  return str.replace(/\{\{\s*([A-Za-z0-9_.]+?)\s*\}\}/g, (match, expr) => {
    const path = expr.trim().split(".");
    let cur = data;
    for (const key of path) {
      cur = cur?.[key];
    }
    return cur == null ? match : String(cur);
  });
}

export const CustomWidget = observer(
  ({
    settings,
    store,
  }: {
    settings: CustomWidgetSettings;
    store: CustomWidgetStore;
  }) => {
    const [html, setHtml] = useState<string>("");
    const [css, setCss] = useState<string>("");
    const [js, setJs] = useState<string>("");
    const [doc, setDoc] = useState<string>("");

    // StreamElements widgets use `window.location.href.includes("streamelements")`
    // to detect the overlay host and skip their non-SE fallback branch. With
    // `srcDoc` the URL is `about:srcdoc`, so the fallback renders first with an
    // empty session; the later `onWidgetLoad` instance then reuses the same
    // component instance (its `useEffect([])` never re-runs) and the widget
    // stays on the empty fallback. Serving the document from a blob URL whose
    // fragment contains "streamelements" makes the host check pass, so only the
    // `onWidgetLoad` path renders.
    const [iframeUrl, setIframeUrl] = useState<string>("");
    useEffect(() => {
      if (!doc) {
        setIframeUrl("");
        return;
      }
      const url = URL.createObjectURL(new Blob([doc], { type: "text/html" }));
      setIframeUrl(`${url}#streamelements`);
      return () => URL.revokeObjectURL(url);
    }, [doc]);

    useEffect(() => {
      settings.configContent().then((config) => {
        settings
          .htmlContent()
          .then((blob) => blob.text())
          .then((text) => resolvePlaceholders(text, config))
          .then((content) => {
            setHtml(content);
          });
        settings
          .jsContent()
          .then((blob) => blob.text())
          .then((text) => resolveJsPlaceholders(text, config))
          .then((text) => {
            setJs(text);
          });
        settings
          .cssContent()
          .then((blob) => blob.text())
          .then((text) => resolvePlaceholders(text, config))
          .then((text) => {
            setCss(text);
          });
      });
      setInterval(() => {
        sendMessageToWorker({ type: "GetWorkersStatus" });
      }, 10000);
    }, [settings]);

    useEffect(() => {
      setDoc(
        `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <script src="https://cdn.jsdelivr.net/npm/animejs/dist/bundles/anime.umd.min.js"></script>
          <script src="https://code.jquery.com/jquery-3.6.0.min.js" integrity="sha256-/xUj+3OJU5yExlq6GSYGSHk7tPXikynS7ogEvDej/m4=" crossorigin="anonymous"></script>
          <style>
            html, body { background: none transparent!important; }
          </style>
          <style>
            ${css}
          </style>
          <style>
            .oda-message-author {
              display: flex;
              flex-direction: row;
              align-items: center;
              width: 100%;
              gap: 3px;
              & img {
                position: initial;
              }
            }
          </style>
        </head>
        <body>
          ${html}
          <script>
            ${js}
          </script>
          <script>
            function getValue(variables, name) {
              return variables.find((it) => it.name === name)?.value ?? null;
            }

            window.addEventListener("load", function (event) {
              const e = new CustomEvent("onWidgetLoad", {
                detail: ${JSON.stringify(store.session)},
              });

              console.log({ session: ${JSON.stringify(store.session)}}, "Sending onWidgetLoad event");
              window.dispatchEvent(e);
              console.log('adding message listener');
              if (typeof SharedWorker === "undefined") {
                console.warn("SharedWorker is not supported — chat messages will not be received");
                return;
              }
              // This document is served from a blob: URL, where relative URLs
              // cannot be resolved, so use the absolute URL computed by the
              // host page. Same URL + same name = same SharedWorker instance.
              const workerUrl = ${JSON.stringify(workerUrl())};
              let worker;
              try {
                worker = new SharedWorker(workerUrl, { name: "logger-worker" });
              } catch (err) {
                console.error("Failed to connect to logger worker", err);
                return;
              }
              worker.port.start();
              worker.port.onmessage = (message) => {
                const event = message.data;
                console.log({event:event}, "Received message");
                if (event._type === "TWITCH_CHAT_MESSAGE" || event._type === "VKLIVE_CHAT_MESSAGE" || event._type === "KICK_CHAT_MESSAGE") {
                  const badges = getValue(event._variables, "badges");
                  const role = getValue(event._variables, "role");
                  const isBroadcaster = role === "broadcaster";
                  const isModerator = role === "moderator" || role === "lead_moderator";
                  const isSubscriber = getValue(event._variables, "isSubscriber");
                  let author = '<span class="oda-message-author"><img height="16" width="16" src="https://assets.twitch.tv/assets/favicon-32-e29e246c157142c94346.png"/><span>' + getValue(event._variables, "chatter_user_login")  + "</span></span>";
                  if (event._type === "VKLIVE_CHAT_MESSAGE") {
                    author = '<span class="oda-message-author"><img height="16" width="16" src="https://dev.live.vkvideo.ru/static/favicon.png"/><span>' + getValue(event._variables, "chatter_user_login")  + "</span></span>";
                  }
                  if (event._type === "KICK_CHAT_MESSAGE") {
                    author = '<span class="oda-message-author"><img height="16" width="16" src="https://kick.com/favicon.ico?favicon.1782phf7eyk2q.ico="/><span>' + getValue(event._variables, "chatter_user_login")  + "</span></span>";
                  }
                  const e = new CustomEvent("onEventReceived", {
                    detail: {
                      listener: "message",
                      event: {
                      renderedText: getValue(event._variables, "message_text"),
                        data: {
                          "time": Date.now(),
                          "tags": {
                            "badges": isBroadcaster ? "broadcaster/1" : "",
                            "color": "#641FEF",
                            "display-name": author,
                            "mod": isModerator ? "1" : "0",
                            "subscriber": isSubscriber ? "1" : "0",
                            "turbo": "0",
                            "user-type": ""
                          },
                          "nick": author,
                          "userId": "123123",
                          "displayColor": "#641FEF",
                          "badges": getValue(event._variables, "badges"),
                          "channel": "channelname",
                          "isAction": false,
                          emotes: getValue(event._variables, "emotes"),
                          "msgId": "885d1f33-8387-4206-a668-e9b1409a99Xb",
                          displayName: author,
                          text: getValue(event._variables, "message_text"),
                        }
                      }
                    }
                  });
                  console.log({event:e}, "Sending CustomWidgetEvent");
                  window.dispatchEvent(e);
                }
              };
            });
          </script>
        </body>
      </html>`,
      );
    }, [html, css, js, store.session]);

    const width =
      settings.widthProperty.value > 0
        ? `${settings.widthProperty.value}px`
        : "100%";
    const height =
      settings.heightProperty.value > 0
        ? `${settings.heightProperty.value}px`
        : "100%";

    return (
      <>
        {iframeUrl && (
          <iframe
            width={width}
            height={height}
            sandbox="allow-scripts allow-same-origin"
            scrolling="no"
            src={iframeUrl}
            style={{ border: "none", background: "transparent" }}
          />
        )}
      </>
    );
  },
);
